import { get, set, del } from 'idb-keyval';
import { SCHEMA_VERSION, emptyPlayerState, type PlayerState } from '../game/types';
import { generateId } from '../game/codes';
import { upgradeLegacyBoards } from '../game/trips';
import type { AuthLink, FriendsCache, StorageAdapter } from './types';
import { LOCKS, withLock } from './lock';

/**
 * IndexedDB-backed source of truth.
 *
 * The recovery code lives under its own key and is deliberately *not* part of
 * PlayerState: it is a credential, the server stores only its hash, and it must
 * never end up inside a synced payload. The auth link is kept out for the same
 * reason - it is per-device, not per-player, and syncing it would push one
 * phone's sign-in onto every other.
 */

const STATE_KEY = 'rego-bingo:player-state';
const RECOVERY_KEY = 'rego-bingo:recovery-code';
const AUTH_KEY = 'rego-bingo:auth';
const FRIENDS_KEY = 'rego-bingo:friends';

/**
 * Brings a stored blob up to the current schema. Migrations live here rather
 * than in the database precisely because the payload is an opaque JSON blob
 * server-side - the client owns its own shape.
 */
function migrate(raw: PlayerState): PlayerState {
  let state = raw;

  if (state.schemaVersion === undefined || state.schemaVersion < 1) {
    state = { ...state, schemaVersion: 1 };
  }

  // Defensive: a partially-written blob should degrade to a playable state
  // rather than crashing the app on load.
  return {
    ...state,
    schemaVersion: SCHEMA_VERSION,
    lifetime: state.lifetime ?? {},
    lifetimeUndone: state.lifetimeUndone ?? {},
    trips: (state.trips ?? []).map((trip) => ({
      ...trip,
      myClaims: trip.myClaims ?? {},
      undone: trip.undone ?? {},
      leaderboard: trip.leaderboard ?? [],
      leaderboardAt: trip.leaderboardAt ?? 0,
      // Trips saved before shared trips existed were local-only by definition.
      registered: trip.registered ?? false,
      isOwner: trip.isOwner ?? true,
      version: trip.version ?? 0,
    })),
    activeTripId: state.activeTripId ?? null,
    version: state.version ?? 0,
  };
}

export const localStorageAdapter: StorageAdapter = {
  async load() {
    const raw = await get<PlayerState>(STATE_KEY);
    // The board upgrade is async, because hashing is, so it sits outside
    // `migrate`. The next save writes the upgraded copy back.
    return raw ? upgradeLegacyBoards(migrate(raw)) : null;
  },
  async save(state) {
    await set(STATE_KEY, state);
  },
  async clear() {
    await del(STATE_KEY);
    await del(RECOVERY_KEY);
    await del(AUTH_KEY);
    await del(FRIENDS_KEY);
  },
};

/**
 * Loads existing state, or creates a brand-new player. Never returns null.
 *
 * Held under a lock because two tabs opening at once would otherwise both find
 * nothing, both create a player, and both save - leaving whichever wrote last
 * as the survivor and the other tab holding a stale id.
 */
export async function loadOrCreate(): Promise<PlayerState> {
  return withLock(LOCKS.bootstrap, async () => {
    const existing = await localStorageAdapter.load();
    if (existing) return existing;

    const created = emptyPlayerState(generateId());
    await localStorageAdapter.save(created);
    return created;
  });
}

export async function getRecoveryCode(): Promise<string | null> {
  return (await get<string>(RECOVERY_KEY)) ?? null;
}

export async function setRecoveryCode(code: string): Promise<void> {
  await set(RECOVERY_KEY, code);
}

/**
 * Forgets the recovery code without touching anything else.
 *
 * Used when this device adopts a different player by signing in: the code that
 * was stored belongs to the player just left behind, and keeping it would offer
 * the player a code that opens somebody else's collection.
 */
export async function clearRecoveryCode(): Promise<void> {
  await del(RECOVERY_KEY);
}

export async function getAuthLink(): Promise<AuthLink | null> {
  return (await get<AuthLink>(AUTH_KEY)) ?? null;
}

export async function setAuthLink(link: AuthLink): Promise<void> {
  await set(AUTH_KEY, link);
}

export async function clearAuthLink(): Promise<void> {
  await del(AUTH_KEY);
}

/** The cached friends board, if it belongs to this player. See `FriendsCache`. */
export async function getFriendsCache(playerId: string): Promise<FriendsCache | null> {
  const stored = await get<FriendsCache>(FRIENDS_KEY);
  return stored?.playerId === playerId ? stored : null;
}

export async function setFriendsCache(cache: FriendsCache): Promise<void> {
  await set(FRIENDS_KEY, cache);
}

/** Replaces all local state, e.g. after restoring from a recovery code. */
export async function replaceState(state: PlayerState): Promise<void> {
  await localStorageAdapter.save(state);
}
